import type { BrandContext, BrandKey } from '@/lib/brand/types';

export const DEFAULT_NYLA_PRIMARY_COLOR = '#8c52ff';
export const DEFAULT_NYLA_SECONDARY_COLOR = '#03989e';
export const DEFAULT_NYLA_FONT_FAMILY = 'Barlow';
export const DEFAULT_SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif';

export const normalizeHexColor = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed || !/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
    return null;
  }

  if (trimmed.length === 4) {
    return `#${trimmed
      .slice(1)
      .split('')
      .map((char) => `${char}${char}`)
      .join('')
      .toLowerCase()}`;
  }

  return trimmed.toLowerCase();
};

export const normalizeBrandFontFamily = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.length > 80) {
    return null;
  }

  return /^[A-Za-z0-9 _-]+$/.test(trimmed)
    ? trimmed.replace(/\s+/g, ' ')
    : null;
};

const quoteFontFamily = (fontFamily: string) =>
  /^[A-Za-z0-9_-]+$/.test(fontFamily) ? fontFamily : `'${fontFamily}'`;

export const getBrandFontFamilyStack = (
  fontFamily?: string | null,
  brandKey: BrandKey = 'explore'
) => {
  if (brandKey !== 'nyla') {
    return DEFAULT_SYSTEM_FONT_STACK;
  }

  const resolvedFontFamily =
    normalizeBrandFontFamily(fontFamily) ?? DEFAULT_NYLA_FONT_FAMILY;
  return `${quoteFontFamily(
    resolvedFontFamily
  )}, Montserrat, system-ui, sans-serif`;
};

const hexToRgb = (value: string) => ({
  r: parseInt(value.slice(1, 3), 16),
  g: parseInt(value.slice(3, 5), 16),
  b: parseInt(value.slice(5, 7), 16),
});

export const getBrandRuntimeThemeStyle = (
  brand: Pick<
    BrandContext,
    'brandKey' | 'fontFamily' | 'primaryColor' | 'secondaryColor'
  >
): Record<string, string> | undefined => {
  if (brand.brandKey !== 'nyla') {
    return undefined;
  }

  const primaryColor =
    normalizeHexColor(brand.primaryColor) ?? DEFAULT_NYLA_PRIMARY_COLOR;
  const secondaryColor =
    normalizeHexColor(brand.secondaryColor) ?? DEFAULT_NYLA_SECONDARY_COLOR;
  const primaryRgb = hexToRgb(primaryColor);
  const secondaryRgb = hexToRgb(secondaryColor);
  const fontStack = getBrandFontFamilyStack(brand.fontFamily, 'nyla');

  return {
    '--brand-font-body': fontStack,
    '--brand-font-display': fontStack,
    '--brand-gradient-hero': `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 48%, ${secondaryColor} 100%)`,
    '--brand-shadow-soft': `0 10px 24px -10px rgb(${primaryRgb.r} ${primaryRgb.g} ${primaryRgb.b} / 0.3)`,
    '--brand-shadow-playful': `0 10px 24px -10px rgb(${secondaryRgb.r} ${secondaryRgb.g} ${secondaryRgb.b} / 0.28)`,
  };
};
